// VENDORED from text-quiet-companion, then FORMALIZED per the finish-line
// prompt B0: their initialData-from-mock pattern becomes explicit
// demo/live modes under VITE_LIFE_MODE (no new layer invented — the same
// hooks, same keys, same screens).
//
//   demo — queryFn serves the mock fixtures from a session-scoped
//          mutable store (writes land in-memory); the __root badge is
//          visible; nothing touches the network.
//   live — queryFn hits /api/life/*, unwraps the backend's keyed
//          envelopes ({bills: [...]}), and adapts wire → view types via
//          lib/adapt.ts (backend contract canonical; see
//          docs/phase-life/CONTRACT_DIFF.md). initialData is EMPTY
//          shapes — never fixtures posing as real data.
//
// Live-path endpoint corrections vs the Lovable draft (CONTRACT_DIFF):
//   notifications → settings/notifications · reminders → appointments ·
//   health/metrics → health/records (series computed at the boundary) ·
//   nudge snooze carries {until} (backend requires it) ·
//   homework/connectors have no v1 backend — demo-only, [] in live.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, life } from "./api";
import { IS_DEMO } from "./mode";
import {
  toBriefing,
  toNudge,
  toHabit,
  toBill,
  toImportantDate,
  toFamilyEvent,
  toJournalEntry,
  toReading,
  toVehicle,
  toShoppingItem,
  toVaultItem,
  toHealthMetrics,
  toReminder,
  toPrefs,
  toFamily,
  toGoal,
  toSpending,
  type WireBriefing,
  type WireNudge,
  type WireHabit,
  type WireBill,
  type WireDate,
  type WireEvent,
  type WireJournal,
  type WireReading,
  type WireVehicle,
  type WireShopping,
  type WireVault,
  type WireHealthRecord,
  type WireAppointment,
  type WirePrefs,
  type WireMember,
  type WireGoal,
  type WireTransaction,
  type SpendingView,
  toLegacyLetter,
  type WireLegacyItem,
  toWebhookRecord,
  type WireWebhookEndpoint,
} from "./adapt";
import type {
  Briefing,
  AgentNudge,
  Bill,
  Family,
  Goal,
  Habit,
  HealthMetric,
  ImportantDate,
  JournalEntry,
  NotificationPrefs,
  Reading,
  Reminder,
  ShoppingItem,
  Vehicle,
  VaultItem,
  FamilyEvent,
  Homework,
  Connector,
  ConnectResponse,
  LegacyLetter,
  WebhookRecord,
} from "@/data/types";
import * as mock from "@/data/mock";

const POLL = 30_000;
const poll = IS_DEMO ? false : POLL;

// ── Demo store (session-scoped; seeded from the vendored fixtures) ──────────

const demo = {
  nudges: structuredClone(mock.agentNudges) as AgentNudge[],
  prefs: {
    smsEnabled: true,
    pushEnabled: true,
    digestCadence: "daily",
    localSuggestions: true,
  } as NotificationPrefs,
};

const EMPTY_BRIEFING: Briefing = {
  greetingName: "",
  todayLabel: "",
  quote: { text: "", author: "" },
  glance: {
    timeLabel: "",
    weather: { tempC: 0, condition: "", high: 0, low: 0, city: "" },
    search: { topic: "", summary: "" },
    agentSuggestions: [],
  },
  nudges: [],
};

// ── Reads ────────────────────────────────────────────────────────────────────

export function useBriefing() {
  return useQuery({
    queryKey: ["briefing"],
    queryFn: IS_DEMO
      ? async (): Promise<Briefing> => ({
          greetingName: mock.greetingName,
          todayLabel: mock.todayLabel,
          quote: mock.quoteOfTheDay,
          glance: mock.glance,
          nudges: demo.nudges,
        })
      : async () => toBriefing(await life.get<WireBriefing>("briefing")),
    initialData: IS_DEMO
      ? ({
          greetingName: mock.greetingName,
          todayLabel: mock.todayLabel,
          quote: mock.quoteOfTheDay,
          glance: mock.glance,
          nudges: mock.agentNudges,
        } as Briefing)
      : EMPTY_BRIEFING,
    refetchInterval: poll,
  });
}

export function useNudges() {
  return useQuery({
    queryKey: ["nudges"],
    queryFn: IS_DEMO
      ? async () => demo.nudges
      : async () => (await life.get<{ nudges: WireNudge[] }>("nudges")).nudges.map(toNudge),
    initialData: IS_DEMO ? (mock.agentNudges as AgentNudge[]) : [],
    refetchInterval: poll,
  });
}

export function useNudgeAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; action: "approve" | "dismiss" | "snooze" }) => {
      if (IS_DEMO) {
        // Demo keeps the card in the list — the screens' local
        // booked/dismissed pills are the designed UX (pixel-identical
        // rule). Live mode refetches and decided nudges drop out.
        return;
      }
      // Backend requires a snooze horizon; default 24h (contract).
      const body =
        v.action === "snooze"
          ? { until: new Date(Date.now() + 86_400_000).toISOString() }
          : undefined;
      await life.post(`nudges/${v.id}/${v.action}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nudges"] });
      qc.invalidateQueries({ queryKey: ["briefing"] });
    },
  });
}

export const useFamily = () =>
  useQuery({
    queryKey: ["family", "members"],
    queryFn: IS_DEMO
      ? async () => mock.family
      : async () => toFamily((await life.get<{ members: WireMember[] }>("family/members")).members),
    initialData: IS_DEMO ? mock.family : ({ parents: [], kids: [] } as Family),
  });

// Raw member profiles for the Household management UI (name, relationship,
// birthday, phone, email). Same endpoint as useFamily, unshaped; the
// ["family", "members"] prefix keeps both in sync on invalidation.
export const useFamilyMembers = () =>
  useQuery({
    queryKey: ["family", "members", "profiles"],
    queryFn: IS_DEMO
      ? async () =>
          [...mock.family.parents, ...mock.family.kids].map(
            (m): WireMember => ({
              id: m.id,
              name: m.name,
              relationship: "age" in m ? "child" : "parent",
              dateOfBirth: null,
            }),
          )
      : async () => (await life.get<{ members: WireMember[] }>("family/members")).members,
    initialData: [] as WireMember[],
  });

// ── Role & member invites (consent-gated — same flow as the Pro Team) ────────

export type LifeRole = "owner" | "admin" | "manager" | "member" | "viewer";

// GET /life/me — the signed-in member's role, for role-aware UI (owner-only
// invite controls, child-safe nav). 'viewer' is the child/teen family role.
export const useMe = () =>
  useQuery({
    queryKey: ["me"],
    queryFn: IS_DEMO
      ? async () => ({ userId: "demo-user", role: "owner" as LifeRole })
      : async () => await life.get<{ userId: string; role: LifeRole }>("me"),
    staleTime: 5 * 60_000,
    retry: false,
  });

export type PendingInvite = {
  id: string;
  email: string | null;
  proposedRole: string;
  invitationSentAt: string | null;
  consentAcceptedAt: string | null;
  expiresAt: string;
};

// GET /api/members/pending — owner-only view of live invites. The endpoint
// is shared with the Pro dashboard (one flow, one table, two callers).
export const usePendingInvites = (enabled: boolean) =>
  useQuery({
    queryKey: ["members", "pending"],
    queryFn: IS_DEMO
      ? async () => [] as PendingInvite[]
      : async () => (await api<{ pending: PendingInvite[] }>("/api/members/pending")).pending,
    enabled,
    initialData: [] as PendingInvite[],
  });

// POST /api/members/invite — email-only, consent-gated. The invited person
// consents and completes intake themselves on the consent page; nothing is
// collected from them before that.
export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { email: string; proposedRole: string }) => {
      if (IS_DEMO) return;
      await api("/api/members/invite", { method: "POST", body: v });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", "pending"] }),
  });
}

// POST /api/members/pending/:id/revoke — deny/cancel a pending invite; its
// consent link stops resolving immediately.
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_DEMO) return;
      await api(`/api/members/pending/${id}/revoke`, { method: "POST" });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", "pending"] }),
  });
}

// ── Family Pulse + Ask Dryvn (Life faces of the Pro dashboard) ──────────────

export type PulseDriver = {
  key: string;
  label: string;
  detail: string;
  factor: number;
  weight: number;
};
export type FamilyPulse = {
  score: number | null;
  label: string;
  drivers: PulseDriver[];
  computedAt: string;
};

// GET /life/pulse — deterministic household health score over real bills,
// habits, and weekly rhythm. Adult-only server-side; callers gate on role.
export const usePulse = (enabled: boolean) =>
  useQuery({
    queryKey: ["pulse"],
    queryFn: IS_DEMO
      ? async (): Promise<FamilyPulse> => ({
          score: 84,
          label: "Humming along",
          drivers: [
            { key: "bills", label: "Bills on time", detail: "All 6 bills current", factor: 1, weight: 40 },
            { key: "habits", label: "Habit consistency", detail: "17 of 21 habit checks this week", factor: 0.81, weight: 40 },
            { key: "rhythm", label: "Household rhythm", detail: "4 events ahead · 3 health logs", factor: 1, weight: 20 },
          ],
          computedAt: "2026-07-05",
        })
      : async () => await life.get<FamilyPulse>("pulse"),
    enabled,
    retry: false,
  });

export type ChatAction = { summary: string; icon?: string };
export type ChatTurn = {
  id: string;
  message: string | null;
  reply: string;
  at: string;
  actions?: ChatAction[];
};

// GET /api/v1/dryvn/chat/history — the same brain SMS uses, read back.
export const useDryvnHistory = (enabled: boolean) =>
  useQuery({
    queryKey: ["dryvn", "history"],
    queryFn: IS_DEMO
      ? async () => [] as ChatTurn[]
      : async () =>
          (await api<{ data?: { turns?: ChatTurn[] } }>("/api/v1/dryvn/chat/history")).data
            ?.turns ?? [],
    enabled,
    initialData: [] as ChatTurn[],
  });

// POST /api/v1/dryvn/chat — a web message through the production SMS brain.
// The reply may take a full model round trip; the fetch has no client
// timeout, matching the endpoint's own pacing.
export function useSendDryvn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (message: string) => {
      if (IS_DEMO) {
        return { reply: "This is the demo — your real Dryvn answers at launch.", actions: [] };
      }
      const r = await api<{ data?: { reply?: string; actions?: ChatAction[] } }>(
        "/api/v1/dryvn/chat",
        // surface declares the turn's domain — the Life app is a Life
        // surface (the Pro dashboard sends 'pro'; see the 2026-08-12
        // dashboard-chat domain-leak fix).
        { method: "POST", body: { message, surface: "life" } },
      );
      return { reply: r.data?.reply ?? "", actions: r.data?.actions ?? [] };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dryvn", "history"] });
    },
  });
}

export type FamilyMemberInput = {
  name: string;
  relationship: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
};

// POST /life/family/members — profile create (BUG-098 household directory).
export function useAddFamilyMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: FamilyMemberInput) => {
      if (IS_DEMO) return;
      await life.post("family/members", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "members"] }),
  });
}

// PUT /life/family/members/:id — partial profile update.
export function useUpdateFamilyMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: FamilyMemberInput & { id: string }) => {
      if (IS_DEMO) return;
      await life.put(`family/members/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "members"] }),
  });
}

// DELETE /life/family/members/:id — soft delete (backend stamps deleted_at).
export function useDeleteFamilyMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_DEMO) return;
      await life.del(`family/members/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "members"] }),
  });
}

export function useToggleHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_DEMO) return; // screens keep their local toggle state in demo
      await life.post(`habits/${id}/toggle`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["habits"] });
      qc.invalidateQueries({ queryKey: ["briefing"] });
    },
  });
}

export const useHabits = () =>
  useQuery({
    queryKey: ["habits"],
    queryFn: IS_DEMO
      ? async () => mock.habits
      : async () => (await life.get<{ habits: WireHabit[] }>("habits")).habits.map(toHabit),
    initialData: IS_DEMO ? mock.habits : ([] as Habit[]),
  });

export const useBills = () =>
  useQuery({
    queryKey: ["bills"],
    queryFn: IS_DEMO
      ? async () => mock.bills
      : async () => (await life.get<{ bills: WireBill[] }>("money/bills")).bills.map(toBill),
    initialData: IS_DEMO ? mock.bills : ([] as Bill[]),
  });

export const useSubscriptions = () =>
  useQuery({
    queryKey: ["bills", "subscriptions"],
    queryFn: IS_DEMO
      ? async () => mock.bills.filter((b) => b.subscription)
      : async () =>
          (await life.get<{ bills: WireBill[] }>("money/bills?recurring=true")).bills
            .map(toBill)
            .filter((b) => b.subscription),
    initialData: IS_DEMO ? mock.bills.filter((b) => b.subscription) : ([] as Bill[]),
  });

export const useGoals = () =>
  useQuery({
    queryKey: ["goals"],
    queryFn: IS_DEMO
      ? async () => mock.goals
      : async () => (await life.get<{ goals: WireGoal[] }>("goals")).goals.map(toGoal),
    initialData: IS_DEMO ? mock.goals : ([] as Goal[]),
  });

export const useSpending = () =>
  useQuery({
    queryKey: ["spending"],
    queryFn: IS_DEMO
      ? async () => mock.spending
      : async () =>
          toSpending(
            (await life.get<{ transactions: WireTransaction[] }>("money/transactions"))
              .transactions,
          ),
    initialData: IS_DEMO
      ? mock.spending
      : ({ monthLabel: "", totalCents: 0, rows: [], recentLog: [] } as SpendingView),
  });

export const useImportantDates = () =>
  useQuery({
    queryKey: ["dates"],
    queryFn: IS_DEMO
      ? async () => mock.importantDates
      : async () => (await life.get<{ dates: WireDate[] }>("dates")).dates.map(toImportantDate),
    initialData: IS_DEMO ? mock.importantDates : ([] as ImportantDate[]),
  });

export const useReminders = () =>
  useQuery({
    queryKey: ["reminders"],
    // Live source: appointments (no standalone reminders endpoint in v1).
    queryFn: IS_DEMO
      ? async () => mock.reminders
      : async () =>
          (await life.get<{ appointments: WireAppointment[] }>("appointments")).appointments.map(
            toReminder,
          ),
    initialData: IS_DEMO ? mock.reminders : ([] as Reminder[]),
    refetchInterval: poll,
  });

export const useFamilyEvents = () =>
  useQuery({
    queryKey: ["family", "events"],
    queryFn: IS_DEMO
      ? async () => mock.familyEvents
      : async () =>
          (await life.get<{ events: WireEvent[] }>("family/events")).events.map(toFamilyEvent),
    initialData: IS_DEMO ? mock.familyEvents : ([] as FamilyEvent[]),
    refetchInterval: poll,
  });

export const useHomework = () =>
  useQuery({
    queryKey: ["family", "homework"],
    // Phase-12 DEFER — no backend in v1. Demo shows fixtures; live shows
    // the honest empty state.
    queryFn: async () => (IS_DEMO ? mock.homework : ([] as Homework[])),
    initialData: IS_DEMO ? mock.homework : ([] as Homework[]),
  });

// Phase-12 DEFER trio (ruling 6): demo shows fixtures; live shows the
// design's own empty states. No v1 backend exists for these.
export const useRewardCharts = () =>
  useQuery({
    queryKey: ["family", "rewards"],
    queryFn: async () => (IS_DEMO ? mock.rewardCharts : []),
    initialData: IS_DEMO ? mock.rewardCharts : [],
  });

export const useBehaviourLog = () =>
  useQuery({
    queryKey: ["family", "behaviour"],
    queryFn: async () => (IS_DEMO ? mock.behaviourLog : []),
    initialData: IS_DEMO ? mock.behaviourLog : [],
  });

export const useElders = () =>
  useQuery({
    queryKey: ["family", "elders"],
    queryFn: async () => (IS_DEMO ? mock.elders : []),
    initialData: IS_DEMO ? mock.elders : [],
  });

export const useJournal = () =>
  useQuery({
    queryKey: ["journal"],
    queryFn: IS_DEMO
      ? async () => mock.journal
      : async () =>
          (await life.get<{ entries: WireJournal[] }>("journal")).entries.map(toJournalEntry),
    initialData: IS_DEMO ? mock.journal : ([] as JournalEntry[]),
  });

export const useReading = () =>
  useQuery({
    queryKey: ["reading"],
    queryFn: IS_DEMO
      ? async () => mock.reading
      : async () => (await life.get<{ items: WireReading[] }>("reading")).items.map(toReading),
    initialData: IS_DEMO ? mock.reading : ([] as Reading[]),
  });

export const useVehicles = () =>
  useQuery({
    queryKey: ["vehicles"],
    queryFn: IS_DEMO
      ? async () => mock.vehicles
      : async () =>
          (await life.get<{ vehicles: WireVehicle[] }>("vehicles")).vehicles.map(toVehicle),
    initialData: IS_DEMO ? mock.vehicles : ([] as Vehicle[]),
  });

export const useShopping = () =>
  useQuery({
    queryKey: ["shopping"],
    queryFn: IS_DEMO
      ? async () => mock.initialShopping
      : async () =>
          (await life.get<{ items: WireShopping[] }>("shopping")).items.map(toShoppingItem),
    initialData: IS_DEMO ? mock.initialShopping : ([] as ShoppingItem[]),
  });

export function useAddShoppingItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { name: string; category: string }) => {
      if (IS_DEMO) return; // screens keep their local list state in demo
      await life.post("shopping", { name: v.name, category: v.category });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopping"] }),
  });
}

// ── Tonight's dinner (src/routes/dashboard/life/kitchen.ts) ─────────────────

export type DinnerIngredient = { name: string; quantity?: string | null };
export type DinnerSuggestion = {
  title: string;
  description: string;
  ingredients: DinnerIngredient[];
  // The real recipe this came from (grounded web search). Absent only when
  // the backend couldn't verify the URL against the search results.
  source?: { name: string; url: string };
};
// suggestion === null is the backend's honest "kitchen's closed" fallback
// when the model rail is down; `message` carries the copy to show.
export type DinnerResponse = {
  suggestion: DinnerSuggestion | null;
  message?: string;
  computedAt: string;
};

// GET /life/dinner — on-demand (a model call costs real latency, so it
// fires on tap, never on page load). A mutation fits cleaner than an
// enabled:false query: "Another idea" is a natural re-mutate and nothing
// caches a stale dinner.
export function useDinnerSuggestion() {
  return useMutation({
    // exclude: dishes the family already saw this session, so "Another
    // idea" genuinely varies (the backend feeds them into the grounded
    // web search as an avoid list).
    mutationFn: async (exclude: string[]): Promise<DinnerResponse> => {
      if (IS_DEMO) {
        return {
          suggestion: {
            title: "One-Pot Creamy Tomato Gnocchi",
            description:
              "Pillowy gnocchi simmered in a tomato-basil cream sauce with spinach folded in at the end. One pot, about 30 minutes, and mild enough for everyone at the table.",
            ingredients: [
              { name: "potato gnocchi", quantity: "500 g" },
              { name: "crushed tomatoes", quantity: "1 can (796 ml)" },
              { name: "heavy cream", quantity: "1/2 cup" },
              { name: "baby spinach", quantity: "2 cups" },
              { name: "garlic", quantity: "3 cloves" },
              { name: "parmesan", quantity: "1/2 cup, grated" },
            ],
            source: { name: "Demo Kitchen", url: "https://example.com/gnocchi" },
          },
          computedAt: new Date().toISOString().slice(0, 10),
        };
      }
      const qs =
        exclude.length > 0 ? `dinner?exclude=${encodeURIComponent(exclude.join(","))}` : "dinner";
      return life.get<DinnerResponse>(qs);
    },
  });
}

// POST /life/dinner/add-to-list — pushes the suggestion's ingredients onto
// the shopping list (one row each, 'grocery' list default), then refetches
// the list so the new rows appear everywhere.
export function useAddIngredients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ingredients: DinnerIngredient[]) => {
      if (IS_DEMO) return;
      await life.post("dinner/add-to-list", {
        ingredients: ingredients.map((i) => ({
          name: i.name,
          ...(i.quantity ? { quantity: i.quantity } : {}),
        })),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopping"] }),
  });
}

// ── School — homework reminders (src/routes/dashboard/life/school.ts) ───────

// Homework v1 is a reminder on the existing dryvn.reminders rail — no
// homework table or tasks-create endpoint exists on the dashboard surface.
// Create-only: the mutation's response is the confirmation, and the
// reminder itself arrives by text via the reminder dispatcher. Nothing to
// invalidate — there is no live homework list to refetch.
export type HomeworkReminderInput = {
  what: string;
  kidMemberId: string;
  /** Absolute ISO instant (offset included) — built client-side from the
   *  picked due date + time, same as calendar startsAt. */
  remindAt: string;
};
export type HomeworkReminder = {
  reminderId: string | null;
  reminderText: string;
  remindAt: string;
};

export function useAddHomework() {
  return useMutation({
    mutationFn: async (v: HomeworkReminderInput): Promise<HomeworkReminder> => {
      if (IS_DEMO) {
        return {
          reminderId: "demo-homework",
          reminderText: `Homework: ${v.what}`,
          remindAt: v.remindAt,
        };
      }
      return (await life.post<{ homework: HomeworkReminder }>("school/homework", v)).homework;
    },
  });
}

export function useSetShoppingDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; done: boolean }) => {
      if (IS_DEMO) return;
      await life.put(`shopping/${v.id}`, { isPurchased: v.done });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopping"] }),
  });
}

export const useVault = () =>
  useQuery({
    queryKey: ["vault"],
    queryFn: IS_DEMO
      ? async () => mock.vault
      : async () => (await life.get<{ items: WireVault[] }>("vault")).items.map(toVaultItem),
    initialData: IS_DEMO ? mock.vault : ([] as VaultItem[]),
  });

export const useLegacyLetters = () =>
  useQuery({
    queryKey: ["legacy"],
    queryFn: IS_DEMO
      ? async () => mock.legacyLetters
      : async () =>
          (await life.get<{ items: WireLegacyItem[] }>("legacy")).items
            .filter((i) => i.itemKind === "letter")
            .map(toLegacyLetter),
    initialData: IS_DEMO ? mock.legacyLetters : ([] as LegacyLetter[]),
  });

export const useHealthMetrics = () =>
  useQuery({
    queryKey: ["health", "metrics"],
    queryFn: IS_DEMO
      ? async () => mock.healthMetrics
      : async () =>
          toHealthMetrics(
            (await life.get<{ records: WireHealthRecord[] }>("health/records")).records,
          ),
    initialData: IS_DEMO ? mock.healthMetrics : ([] as HealthMetric[]),
  });

export const useNotificationPrefs = () =>
  useQuery({
    queryKey: ["notifications"],
    queryFn: IS_DEMO
      ? async () => demo.prefs
      : async () =>
          toPrefs(
            (await life.get<{ notifications: WirePrefs }>("settings/notifications")).notifications,
          ),
    initialData: IS_DEMO
      ? demo.prefs
      : ({
          smsEnabled: true,
          pushEnabled: true,
          digestCadence: "daily" as const,
          localSuggestions: false,
        } as NotificationPrefs),
  });

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: Partial<NotificationPrefs>) => {
      if (IS_DEMO) {
        demo.prefs = { ...demo.prefs, ...prefs };
        return demo.prefs;
      }
      // Ruling 2: all four fields are backend-owned in the config row.
      const body: Record<string, unknown> = {};
      if (prefs.localSuggestions !== undefined) {
        body["localSuggestionsEnabled"] = prefs.localSuggestions;
      }
      if (prefs.smsEnabled !== undefined) body["smsEnabled"] = prefs.smsEnabled;
      if (prefs.pushEnabled !== undefined) body["pushEnabled"] = prefs.pushEnabled;
      if (prefs.digestCadence !== undefined) body["digestCadence"] = prefs.digestCadence;
      await life.put("settings/notifications", body);
      return prefs as NotificationPrefs;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export const useWebhooks = () =>
  useQuery({
    queryKey: ["webhooks"],
    queryFn: IS_DEMO
      ? async () => mock.webhooks
      : async () =>
          (await life.get<{ endpoints: WireWebhookEndpoint[] }>("webhooks")).endpoints.map(
            toWebhookRecord,
          ),
    initialData: IS_DEMO ? mock.webhooks : ([] as WebhookRecord[]),
  });

export const useConnectors = () =>
  useQuery({
    queryKey: ["connectors"],
    // Live: GET /api/life/connectors returns one entry per V1 surface with
    // its real status (src/routes/dashboard/life/connectors.ts). Demo has no
    // backend, so it stays an empty list.
    queryFn: IS_DEMO
      ? async () => [] as Connector[]
      : async () => (await life.get<{ connectors: Connector[] }>("connectors")).connectors,
    initialData: [] as Connector[],
  });

export function useConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ConnectResponse> => {
      if (IS_DEMO) return { status: "pending" };
      return life.post<ConnectResponse>(`connectors/${id}/connect`);
    },
    onSuccess: (res) => {
      if (res?.redirect_url) window.location.href = res.redirect_url;
      qc.invalidateQueries({ queryKey: ["connectors"] });
    },
  });
}

// POST /connectors/:id/sync — force an immediate pull. Backend returns the
// honest outcome ({ status, imported, updated }); the screen renders it.
// Demo has no backend, so it reports a no-op success.
export type SyncResult = {
  status: "connected" | "action-needed";
  imported: number;
  updated: number;
};

export function useSyncConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<SyncResult> => {
      if (IS_DEMO) return { status: "connected", imported: 0, updated: 0 };
      return life.post<SyncResult>(`connectors/${id}/sync`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });
}

// POST /connectors/:id/disconnect — drops the grant (best-effort revoke at
// Google). Demo is a no-op success.
export function useDisconnectConnector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<{ status: string; found?: boolean }> => {
      if (IS_DEMO) return { status: "available" };
      return life.post<{ status: string; found?: boolean }>(`connectors/${id}/disconnect`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });
}

// iCloud connects with a credential form, NOT an OAuth redirect.
// POST /connectors/icloud/connect { appleId, appPassword } →
//   200 { status: "connected" } · 401 CONNECTOR_AUTH_FAILED ·
//   400 MISSING_CREDENTIALS · 503 CONNECTOR_NOT_PROVISIONED.
// The appPassword is an app-specific password generated at appleid.apple.com —
// never the real Apple password. Demo is a no-op success (no backend).
export function useConnectICloud() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      appleId: string;
      appPassword: string;
    }): Promise<{ status: string }> => {
      if (IS_DEMO) return { status: "connected" };
      return life.post<{ status: string }>("connectors/icloud/connect", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });
}

// Plaid connects through Plaid Link, NOT an OAuth redirect. Two-step:
//   1) POST /connectors/plaid/connect → { status: "pending", link_token }
//      the SPA opens Plaid Link with that token.
//   2) POST /connectors/plaid/exchange { public_token, institution_name? }
//      → { status: "connected" } once Link hands back a public token.
export type PlaidConnectResponse = { status: "pending"; link_token?: string };

export function useConnectPlaid() {
  return useMutation({
    mutationFn: async (): Promise<PlaidConnectResponse> => {
      if (IS_DEMO) return { status: "pending" };
      return life.post<PlaidConnectResponse>("connectors/plaid/connect");
    },
  });
}

export function useExchangePlaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      public_token: string;
      institution_name?: string;
    }): Promise<{ status: string }> => {
      if (IS_DEMO) return { status: "connected" };
      return life.post<{ status: string }>("connectors/plaid/exchange", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });
}

// ── Writes (web-entry parity with SMS) ──────────────────────────────────────
// Each mirrors the useAddShoppingItem contract: demo is a no-op (fixtures
// are immutable; screens layer their own optimistic state), live POSTs the
// EXACT backend schema (src/routes/dashboard/life/*.ts) then invalidates the
// hub's read key so the new row appears on refetch.

// POST /life/money/transactions — amountCents is SIGNED (expense negative).
export function useAddTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      amountCents: number;
      category?: string;
      merchant?: string;
      note?: string;
      postedOn?: string;
    }) => {
      if (IS_DEMO) return;
      await life.post("money/transactions", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spending"] }),
  });
}

// POST /life/money/accounts.
export function useAddAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      name: string;
      accountKind?: string;
      institution?: string;
      balanceCents?: number;
    }) => {
      if (IS_DEMO) return;
      await life.post("money/accounts", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

// POST /life/money/budgets (upsert by category).
export function useAddBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { category: string; monthlyCents: number }) => {
      if (IS_DEMO) return;
      await life.post("money/budgets", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["budgets"] }),
  });
}

// POST /life/journal.
export function useAddJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      content: string;
      entryType?: "journal" | "gratitude" | "mood";
      moodScore?: number;
      moodLabel?: string;
    }) => {
      if (IS_DEMO) return;
      await life.post("journal", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journal"] }),
  });
}

// PATCH /life/journal/:id — BL-005: elaborate on an entry after the fact.
export function useElaborateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; addition: string }) => {
      if (IS_DEMO) return;
      await life.patch(`journal/${v.id}`, { addition: v.addition });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journal"] }),
  });
}

// POST /life/reading.
export function useAddReading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      title: string;
      author?: string;
      status?: "want_to_read" | "reading" | "finished" | "abandoned";
      totalPages?: number;
    }) => {
      if (IS_DEMO) return;
      await life.post("reading", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reading"] }),
  });
}

// POST /life/health/records — valueNumeric OR valueText required.
export function useAddHealthRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      metricType: string;
      valueNumeric?: number;
      valueText?: string;
      unit?: string;
      notes?: string;
    }) => {
      if (IS_DEMO) return;
      await life.post("health/records", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["health", "metrics"] }),
  });
}

// POST /life/family/events — startsAt is an ISO datetime with offset.
export function useAddFamilyEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      title: string;
      startsAt: string;
      location?: string;
      eventKind?: "family" | "school" | "sports" | "appointment" | "date_night" | "carpool" | "other";
      attendeeMemberIds?: string[];
    }) => {
      if (IS_DEMO) return;
      await life.post("family/events", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "events"] }),
  });
}

// PUT /life/family/events/:id — the backend's house verb for updates.
// attendeeMemberIds REPLACES the invited set ([] clears it back to the
// whole household). Omitted fields are left unchanged.
export function useUpdateFamilyEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      title?: string;
      startsAt?: string;
      eventKind?: "family" | "school" | "sports" | "appointment" | "date_night" | "carpool" | "other";
      attendeeMemberIds?: string[];
    }) => {
      if (IS_DEMO) return;
      const { id, ...body } = v;
      await life.put(`family/events/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "events"] }),
  });
}

// DELETE /life/family/events/:id — soft delete (backend stamps deleted_at).
export function useDeleteFamilyEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_DEMO) return;
      await life.del(`family/events/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "events"] }),
  });
}

// POST /life/family/contacts.
export function useAddContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      name: string;
      relation?: string;
      phone?: string;
      email?: string;
      notes?: string;
    }) => {
      if (IS_DEMO) return;
      await life.post("family/contacts", v);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["family", "contacts"] }),
  });
}

// Vault signed URL (short-lived) — GET /api/life/vault/:id/url.
// Backend returns 501 vault_url_pending_infra until the GCS session.
export const fetchVaultUrl = (id: string) =>
  life.get<{ url: string; expiresAt: string }>(`vault/${id}/url`);

// ── Money hub live reads (accounts, budgets) ────────────────────────────────
// Passthrough shapes — the backend contract (src/life/contract.ts) is
// canonical, so like useConnectors these render directly with no view
// adapter. Keys match the invalidations in useAddAccount / useAddBudget so a
// web-logged account or budget shows up on the next refetch. Demo has no
// backend for these, so it stays an empty list (the screen's honest empty
// state renders, never fixtures posing as real balances).

// GET /life/money/accounts — one row per account with its signed balance
// (liabilities carry negative balances; 0086 §2).
export interface AccountView {
  id: string;
  name: string;
  accountKind: string;
  institution: string | null;
  balanceCents: number;
  includeInNetWorth: boolean;
}

export const useAccounts = () =>
  useQuery({
    queryKey: ["accounts"],
    queryFn: IS_DEMO
      ? async () => [] as AccountView[]
      : async () => (await life.get<{ accounts: AccountView[] }>("money/accounts")).accounts,
    initialData: [] as AccountView[],
  });

// GET /life/money/budgets — monthly TEMPLATE cap per category, joined with
// month-to-date spend the backend derives (spentThisMonthCents; never
// stored). Spent-vs-cap is real; nothing is invented.
export interface BudgetView {
  id: string;
  category: string;
  monthlyCents: number;
  spentThisMonthCents?: number;
}

export const useBudgets = () =>
  useQuery({
    queryKey: ["budgets"],
    queryFn: IS_DEMO
      ? async () => [] as BudgetView[]
      : async () => (await life.get<{ budgets: BudgetView[] }>("money/budgets")).budgets,
    initialData: [] as BudgetView[],
  });
